'use client'

import { useState } from 'react'

import { LinkField, linkUrl } from '@/lib/link'

import styles from './Footer.module.css'

export interface ExtendedLinkField {
  title: string
  link?: LinkField
  items?: ExtendedLinkField[]
}
export type FooterProps = {
  desktopMenu?: {
    title: string
    link?: LinkField
    items: ExtendedLinkField[]
  }[]
  mobileMenu?: ExtendedLinkField[]
  socialMenu?: {
    icon: string
    link: LinkField
  }[]
  copy: string
  disclaimer: React.ReactNode
}

export const PhnxFooter: React.FC<FooterProps> = ({
  desktopMenu,
  mobileMenu,
  socialMenu,
  copy,
  disclaimer,
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set())

  const toggleExpanded = (index: number) => {
    const newExpanded = new Set(expandedItems)
    if (newExpanded.has(index)) {
      newExpanded.delete(index)
    } else {
      newExpanded.add(index)
    }
    setExpandedItems(newExpanded)
  }

  return (
    <div className={styles['footer']}>
      <footer>
        <div className="container">
          <div className={`${styles.menu} ${styles.desktop}`}>
            {desktopMenu && (
              <ul>
                {desktopMenu.map((item, index) => (
                  <li key={index}>
                    <span>{item.title}</span>
                    <ul>
                      {item.items.map((subItem, index) => (
                        <li key={index}>
                          {subItem.link &&
                          subItem.link.cached_url &&
                          subItem.link.cached_url !== '/en-nz/' ? (
                            <a href={linkUrl(subItem.link)}>{subItem.title}</a>
                          ) : (
                            <span>{subItem.title}</span>
                          )}

                          {subItem.items && (
                            <ul>
                              {subItem.items.map((subSubItem, index) => (
                                <li key={index}>
                                  {subSubItem.link && subSubItem.link.cached_url && (
                                    <a href={linkUrl(subSubItem.link)}>{subSubItem.title}</a>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {mobileMenu && (
            <div className={`${styles.menu} ${styles.mobile}`}>
              <ul>
                {mobileMenu.map((item, index) => (
                  <li key={index}>
                    <div className={styles.mobileMenuItem}>
                      {item.items && item.items.length > 0 ? (
                        <span className={styles.mobileMenuLink}>{item.title}</span>
                      ) : item.link &&
                        item.link.cached_url &&
                        item.link.cached_url !== '/en-nz/' ? (
                        <a href={linkUrl(item.link)} className={styles.mobileMenuLink}>
                          {item.title}
                        </a>
                      ) : (
                        <span className={styles.mobileMenuLink}>{item.title}</span>
                      )}

                      {item.items && item.items.length > 0 && (
                        <button
                          onClick={() => toggleExpanded(index)}
                          className={styles.expandButton}
                          aria-expanded={expandedItems.has(index)}
                          aria-label={`${expandedItems.has(index) ? 'Collapse' : 'Expand'} ${item.title} menu`}
                        ></button>
                      )}
                    </div>

                    {item.items && item.items.length > 0 && (
                      <ul
                        className={`${styles.subMenu} ${expandedItems.has(index) ? styles.expanded : styles.collapsed}`}
                        aria-hidden={!expandedItems.has(index)}
                      >
                        {item.items.map((subItem, subIndex) => (
                          <li key={subIndex}>
                            {subItem.items && subItem.items.length > 0 ? (
                              <>
                                {subItem.link && subItem.link.cached_url ? (
                                  <a
                                    href={linkUrl(subItem.link)}
                                    className={styles.categoryHeading}
                                  >
                                    {subItem.title}
                                  </a>
                                ) : (
                                  <span className={styles.categoryHeading}>{subItem.title}</span>
                                )}
                                <ul className={styles.nestedSubMenu}>
                                  {subItem.items.map((nestedItem, nestedIndex) => (
                                    <li key={nestedIndex}>
                                      {nestedItem.link && nestedItem.link.cached_url && (
                                        <a
                                          href={linkUrl(nestedItem.link)}
                                          className={styles.nestedSubMenuLink}
                                        >
                                          {nestedItem.title}
                                        </a>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              </>
                            ) : subItem.link && subItem.link.cached_url ? (
                              <a href={linkUrl(subItem.link)} className={styles.subMenuLink}>
                                {subItem.title}
                              </a>
                            ) : (
                              <span className={styles.categoryHeading}>{subItem.title}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className={styles.bottom}>
          <div className={styles.social}>
            {socialMenu && (
              <ul>
                {socialMenu.map((item, index) => (
                  <li key={index}>
                    <a href={linkUrl(item.link)}>
                      <span className={`fx-icon ${styles['icon']} ${styles['right']} `}>
                        <span className={`fx-icon ${item.icon}`}></span>
                        <span className="sr-hidden">{item.link.cached_url} link</span>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className={styles.backtotop}>
            <a href="#top">
              <div className={styles.icon}>
                <span className={`fx-icon`}>
                  <span className={`fx-icon fx-icon-arrow-up-lg`}></span>
                </span>
              </div>
              <div className={styles.text}>Back to top</div>
            </a>
          </div>
        </div>
        <div className={styles.disclaimer}>
          {copy != null && copy !== '' && <p>{copy}</p>}
          {disclaimer != null && disclaimer}
        </div>
      </footer>
    </div>
  )
}
